import { userEntryToNumber } from '@ebiz/pretty-num';
import { formatFieldValue } from '../../utils/format';
import SpecificationInput from '../inputs/specification';
import IndexModel from '../index';
import FilterConstants from '../../filter-constants';
import { VVtoDB } from '../utils/conversion';
import { camelize } from '@ember/string';
import type StageDetailResults from '../results/sub-results/stage-details';
import { FilterType, filters } from '../../models/filter/filter';

const _lowPassDefaults = {
    gain: 0,
    gainUnit: FilterConstants.gainUnits.db,
    filterResponseSlider: 50,
    passbandAttenuation: -3,
    stopbandAttenuation: -40,
    selectedResponseType: 'Butterworth',
    passbandFrequency: 10000,
    stopbandFrequency: 40000,
    centerFrequency: undefined,
    filterResponseValue: null,
  },
  _highPassDefaults = {
    gain: 0,
    gainUnit: FilterConstants.gainUnits.db,
    filterResponseSlider: 50,
    passbandAttenuation: -3,
    stopbandAttenuation: -40,
    selectedResponseType: 'Butterworth',
    passbandFrequency: 10000,
    stopbandFrequency: 2500,
    centerFrequency: undefined,
    filterResponseValue: null,
  },
  _bandPassDefaults = {
    gain: 0,
    gainUnit: FilterConstants.gainUnits.db,
    filterResponseSlider: 50,
    passbandAttenuation: -3,
    stopbandAttenuation: -40,
    selectedResponseType: 'Butterworth',
    passbandFrequency: 5000,
    stopbandFrequency: 40000,
    centerFrequency: 10000,
    filterResponseValue: null,
  },
  _limits = {
    // in V/V
    gainVV: { minimum: 1, maximum: 100 },
    passbandAttenuation: { minimum: -20, maximum: -0.1 },
    stopbandAttenuation: { minimum: -100, maximum: -1 },
    passbandFrequency: {
      lowPass: { minimum: 0.1, maximum: 50000000 },
      highPass: { minimum: 0.01, maximum: 1000000 },
      bandPass: { minimum: 0.01, maximum: 2000000 },
    },
    stopbandFrequency: {
      lowPass: { minimum: 0.1, maximum: 500000000 },
      highPass: { minimum: 0.001, maximum: 1000000 },
      bandPass: { minimum: 1, maximum: 100000000 },
    },
    centerFrequency: { minimum: 0.1, maximum: 10000000 },
  };

////////////////////////////////////////////////////////////////////////////////////
// specification - Advisor that runs when user goes into the specifications tab   //
////////////////////////////////////////////////////////////////////////////////////

export default class SpecificationAdvisor {
  model: IndexModel;

  constructor(model: IndexModel) {
    this.model = model;
  }

  get filterType(): string {
    return this.model.inputs.filterType;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // User Functions                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  // Creates the specifications input model
  // Inputs:
  //   - data : input model values, if this is null, defaults are used
  // Returns: specifications input model
  generate(data: SpecificationInput): SpecificationInput {
    let result = data;
    if (!data) {
      const filterName = this.model.inputs.filterType;
      result = _lowPassDefaults;
      if (filterName === FilterConstants.filterType.highPass) {
        result = _highPassDefaults;
      } else if (filterName === FilterConstants.filterType.bandPass) {
        result = _bandPassDefaults;
      }
    }
    return new SpecificationInput(result);
  }

  // Creates a Copy of the Model
  // Inputs:
  //   - sourceModel : model to be copied from
  // Returns: result - a newly created input model with values copied from the source model
  clone(sourceModel: SpecificationInput): SpecificationInput {
    const result = new SpecificationInput();
    result.passbandAttenuation = sourceModel.passbandAttenuation;
    result.stopbandAttenuation = sourceModel.stopbandAttenuation;
    result.filterResponseSlider = sourceModel.filterResponseSlider;
    result.filterResponseValue = sourceModel.filterResponseValue;
    result.selectedResponseType = sourceModel.selectedResponseType;
    result.passbandFrequency = sourceModel.passbandFrequency;
    result.stopbandFrequency = sourceModel.stopbandFrequency;
    result.centerFrequency = sourceModel.centerFrequency;
    result.gain = sourceModel.gain;
    result.gainUnit = sourceModel.gainUnit;
    return result;
  }

  // Validate Specification Inputs
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - Array of error messages
  validate(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): Array<ValidationError | null> {
    let result: Array<ValidationError | null> = [];
    result.push(this._validateGain(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validatePassbandFrequency(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validateStopbandFrequency(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validatePassbandAttenuation(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validateStopbandAttenuation(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validateCenterFrequency(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validatePassbandVsStopbandFrequency(specificationsInput, nameArrayOfFailedFields));
    result.push(this._validatePassbandVsStopbandAttenuation(specificationsInput, nameArrayOfFailedFields));
    result = result.filter((p) => {
      return !!p;
    });
    if (result.length === 0) {
      const errorAttenuation = this._validatePassbandFrequencyVsPassbandAttenuation(
        specificationsInput,
        nameArrayOfFailedFields,
      );
      if (errorAttenuation !== null) result.push(errorAttenuation);
      if (result.length === 0) {
        const errorOrder = this._validateOrder(specificationsInput, nameArrayOfFailedFields);
        if (errorOrder !== null) result.push(errorOrder);
        if (result.length === 0) {
          const errorQ = this._validateQ(specificationsInput, nameArrayOfFailedFields);
          if (errorQ !== null) result.push(errorQ);
          if (result.length === 0) {
            const errorFp = this._validateFp(specificationsInput, nameArrayOfFailedFields);
            if (errorFp !== null) result.push(errorFp);
          }
        }
      }
    }
    return result;
  }

  // Validates Gain Value
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for gain (if any)
  _validateGain(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.gain) === -1) {
      const limits = Object.assign({}, _limits.gainVV);
      if (specificationsInput.gainUnit === FilterConstants.gainUnits.db) {
        limits.minimum = VVtoDB(limits.minimum);
        limits.maximum = VVtoDB(limits.maximum);
      }
      if (specificationsInput.gain < limits.minimum || specificationsInput.gain > limits.maximum) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [FilterConstants.fields.inputs.specifications.gain],
          id: 'gainNotInRange',
          parameters: {
            minimum: formatFieldValue(limits.minimum, FilterConstants.fieldTypes.NumericDecimal, '3s'),
            maximum: formatFieldValue(limits.maximum, FilterConstants.fieldTypes.NumericDecimal, '3s'),
            value: formatFieldValue(specificationsInput.gain, FilterConstants.fieldTypes.NumericDecimal, '3s'),
            unit: specificationsInput.gainUnit,
          },
        };
      }
    }
    return result;
  }

  // Validates Passband Frequency Value
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for passband frequency (if any)
  _validatePassbandFrequency(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.passbandFrequency) === -1) {
      const filterName: 'lowPass' | 'highPass' | 'bandPass' = this.model.inputs.filterType;
      if (
        specificationsInput.passbandFrequency < _limits.passbandFrequency[filterName].minimum ||
        specificationsInput.passbandFrequency > _limits.passbandFrequency[filterName].maximum
      ) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [FilterConstants.fields.inputs.specifications.passbandFrequency],
          id: 'passbandFrequencyNotInRange',
          parameters: {
            minimum: formatFieldValue(
              _limits.passbandFrequency[filterName].minimum,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ),
            maximum: formatFieldValue(
              _limits.passbandFrequency[filterName].maximum,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ),
            value: formatFieldValue(
              specificationsInput.passbandFrequency,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ),
          },
        };
      }
    }
    return result;
  }

  // Validates Passband Attenuation Value
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for passband attenuation (if any)
  _validatePassbandAttenuation(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.passbandAttenuation) === -1) {
      if (
        specificationsInput.passbandAttenuation < _limits.passbandAttenuation.minimum ||
        specificationsInput.passbandAttenuation > _limits.passbandAttenuation.maximum
      ) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [FilterConstants.fields.inputs.specifications.passbandAttenuation],
          id: 'passbandAttenuationNotInRange',
          parameters: {
            minimum: formatFieldValue(
              _limits.passbandAttenuation.minimum,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
            maximum: formatFieldValue(
              _limits.passbandAttenuation.maximum,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
            value: formatFieldValue(
              specificationsInput.passbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
          },
        };
      }
    }
    return result;
  }

  // Validates if Passband Attenuation > Minimum Value at Minimum Low Pass Frequency
  // or Maximum High Pass / Band Pass Frequency
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for passband attenuation (if any)
  _validatePassbandFrequencyVsPassbandAttenuation(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    const filter = this.model && this.model.inputs ? this.model.inputs.filterType : null;
    if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.passbandAttenuation) === -1) {
      let passbandFrequencyRatio = null;
      let frequencyLimit = null;
      if (filter === FilterConstants.filterType.lowPass) {
        passbandFrequencyRatio = specificationsInput.passbandFrequency / _limits.passbandFrequency.lowPass.minimum;
        frequencyLimit = _limits.passbandFrequency.lowPass.minimum;
      } else if (filter === FilterConstants.filterType.bandPass) {
        passbandFrequencyRatio = _limits.passbandFrequency.bandPass.maximum / specificationsInput.passbandFrequency;
        frequencyLimit = _limits.passbandFrequency.bandPass.maximum;
      } else {
        passbandFrequencyRatio = _limits.passbandFrequency.highPass.maximum / specificationsInput.passbandFrequency;
        frequencyLimit = _limits.passbandFrequency.highPass.maximum;
      }
      const frequencyDependentAttenuationLimit = -1 * 20 * Math.log10(passbandFrequencyRatio / 0.707);
      if (specificationsInput.passbandAttenuation < frequencyDependentAttenuationLimit) {
        const errorTemplateMessage =
          filter === FilterConstants.filterType.lowPass
            ? 'passbandAttenuationLowGreaterThan'
            : 'passbandAttenuationHighBandGreaterThan';
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [
            FilterConstants.fields.inputs.specifications.passbandAttenuation,
            FilterConstants.fields.inputs.specifications.passbandFrequency,
          ],
          id: errorTemplateMessage,
          parameters: {
            minimum: formatFieldValue(
              frequencyDependentAttenuationLimit,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
            maximum: formatFieldValue(_limits.passbandAttenuation, FilterConstants.fieldTypes.NumericDecimal, '3s'),
            frequency: formatFieldValue(frequencyLimit, FilterConstants.fieldTypes.Engineering, '3s'),
            value: formatFieldValue(
              specificationsInput.passbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
          },
        };
      }
    }
    return result;
  }

  // Validates Stopband Frequency Value
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for stopband frequency (if any)
  _validateStopbandFrequency(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.stopbandFrequency) === -1) {
      const filterName: 'lowPass' | 'highPass' | 'bandPass' = this.model.inputs.filterType;
      if (
        specificationsInput.stopbandFrequency < _limits.stopbandFrequency[filterName].minimum ||
        specificationsInput.stopbandFrequency > _limits.stopbandFrequency[filterName].maximum
      ) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [FilterConstants.fields.inputs.specifications.stopbandFrequency],
          id: 'stopbandFrequencyNotInRange',
          parameters: {
            minimum: formatFieldValue(
              _limits.stopbandFrequency[filterName].minimum,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ),
            maximum: formatFieldValue(
              _limits.stopbandFrequency[filterName].maximum,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ),
            value: formatFieldValue(
              specificationsInput.stopbandFrequency,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ),
          },
        };
      }
    }
    return result;
  }

  // Validates Stopband Attenuation Value
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for stopband attenuation (if any)
  _validateStopbandAttenuation(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.stopbandAttenuation) === -1) {
      if (
        specificationsInput.stopbandAttenuation < _limits.stopbandAttenuation.minimum ||
        specificationsInput.stopbandAttenuation > _limits.stopbandAttenuation.maximum
      ) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [FilterConstants.fields.inputs.specifications.stopbandAttenuation],
          id: 'stopbandAttenuationNotInRange',
          parameters: {
            minimum: formatFieldValue(
              _limits.stopbandAttenuation.minimum,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
            maximum: formatFieldValue(
              _limits.stopbandAttenuation.maximum,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
            value: formatFieldValue(
              specificationsInput.stopbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
          },
        };
      }
    }
    return result;
  }

  // Validates Center Frequency Value
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for center frequency (if any)
  _validateCenterFrequency(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (this.model.inputs.filterType === FilterConstants.filterType.bandPass) {
      if (nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.centerFrequency) === -1) {
        if (
          specificationsInput.centerFrequency < _limits.centerFrequency.minimum ||
          specificationsInput.centerFrequency > _limits.centerFrequency.maximum
        ) {
          result = {
            tab: FilterConstants.tabs.specifications,
            fields: [FilterConstants.fields.inputs.specifications.centerFrequency],
            id: 'centerFrequencyNotInRange',
            parameters: {
              minimum: formatFieldValue(_limits.centerFrequency.minimum, FilterConstants.fieldTypes.Engineering, '3s'),
              maximum: formatFieldValue(_limits.centerFrequency.maximum, FilterConstants.fieldTypes.Engineering, '3s'),
              value: formatFieldValue(
                specificationsInput.centerFrequency,
                FilterConstants.fieldTypes.Engineering,
                '3s',
              ),
            },
          };
        }
      }
    }
    return result;
  }

  // Validates if Passband Frequncy >= Stopband Frequency (For Low Pass & Band Pass Filters)
  // or if Passband Frequncy <= Stopband Frequency (For High Pass Filters)
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for comparing passband and stopband frequency (if any)
  _validatePassbandVsStopbandFrequency(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    const filter = this.model && this.model.inputs ? this.model.inputs.filterType : null;
    if (
      nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.passbandFrequency) === -1 &&
      nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.stopbandFrequency) === -1
    ) {
      if (filter === FilterConstants.filterType.lowPass || filter === FilterConstants.filterType.bandPass) {
        if (specificationsInput.passbandFrequency >= specificationsInput.stopbandFrequency) {
          result = {
            tab: FilterConstants.tabs.specifications,
            fields: [
              FilterConstants.fields.inputs.specifications.passbandFrequency,
              FilterConstants.fields.inputs.specifications.stopbandFrequency,
            ],
            id: 'passbandIsGreaterThanStopband',
            parameters: {
              passbandFrequency: formatFieldValue(
                specificationsInput.passbandFrequency,
                FilterConstants.fieldTypes.Engineering,
                '3s',
              ),
              stopbandFrequency: formatFieldValue(
                specificationsInput.stopbandFrequency,
                FilterConstants.fieldTypes.Engineering,
                '3s',
              ),
            },
          };
        }
      } else {
        if (specificationsInput.passbandFrequency <= specificationsInput.stopbandFrequency) {
          result = {
            tab: FilterConstants.tabs.specifications,
            fields: [
              FilterConstants.fields.inputs.specifications.passbandFrequency,
              FilterConstants.fields.inputs.specifications.stopbandFrequency,
            ],
            id: 'stopbandIsGreaterThanPassband',
            parameters: {
              passbandFrequency: formatFieldValue(
                specificationsInput.passbandFrequency,
                FilterConstants.fieldTypes.Engineering,
                '3s',
              ),
              stopbandFrequency: formatFieldValue(
                specificationsInput.stopbandFrequency,
                FilterConstants.fieldTypes.Engineering,
                '3s',
              ),
            },
          };
        }
      }
    }
    return result;
  }

  // Validates if Stopband Attenuation >= Passband Attenuation
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for comparing passband and stopband attenuation (if any)
  _validatePassbandVsStopbandAttenuation(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (
      nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.passbandAttenuation) === -1 &&
      nameArrayOfFailedFields.indexOf(FilterConstants.fields.inputs.specifications.stopbandAttenuation) === -1
    ) {
      if (specificationsInput.stopbandAttenuation >= specificationsInput.passbandAttenuation) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [
            FilterConstants.fields.inputs.specifications.passbandAttenuation,
            FilterConstants.fields.inputs.specifications.stopbandAttenuation,
          ],
          id: 'stopbandIsGreaterThanPassbandAttenuation',
          parameters: {
            passbandAttenuation: formatFieldValue(
              specificationsInput.passbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
            stopbandAttenuation: formatFieldValue(
              specificationsInput.stopbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ),
          },
        };
      }
    }
    return result;
  }

  // Validates Filter Order (must be a number and must not be greater than 10)
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for filter order (if any)
  _validateOrder(
    specificationsInput: SpecificationInput,
    nameArrayOfFailedFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.length === 0) {
      // const filter = Filter.create({
      //   selectedFilterType: this.model.inputs.filterType,
      //   selectedResponseType: specificationsInput.selectedResponseType,
      // });
      // const filters: Filter = { lowPass: LowPass, highPass: HighPass, bandPass: BandPass };
      let order = null;
      try {
        order = filters[this.model.inputs.filterType as keyof FilterType].getOrder({
          filterResponseType: specificationsInput.selectedResponseType,
          passbandFrequency: specificationsInput.passbandFrequency,
          passbandAttenuation: specificationsInput.passbandAttenuation,
          stopbandFrequency: specificationsInput.stopbandFrequency,
          stopbandAttenuation: specificationsInput.stopbandAttenuation,
          centerFrequency: specificationsInput.centerFrequency,
          filterResponseValue: specificationsInput.filterResponseValue,
        });
      } catch (e) {
        //eslint-disable no-empty
      }
      if (!order || order <= 0 || order > FilterConstants.maxOrder) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [
            FilterConstants.fields.inputs.specifications.passbandAttenuation,
            FilterConstants.fields.inputs.specifications.stopbandAttenuation,
            FilterConstants.fields.inputs.specifications.passbandFrequency,
            FilterConstants.fields.inputs.specifications.stopbandFrequency,
            'filterResponse',
          ],
          id: 'highOrderFiltersUnsupported',
          parameters: { order: 'N' },
        };
      }
    }
    return result;
  }

  // Validates if Q does not exceed 50
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for Q factor (if any)
  _validateQ(specificationsInput: SpecificationInput, nameArrayOfFailedFields: Array<string>): ValidationError | null {
    let result = null;
    if (nameArrayOfFailedFields.length === 0) {
      const indexModel = new IndexModel(this.model.opAmpSource);
      indexModel.inputs.filterType = this.model.inputs.filterType;
      indexModel.inputs.specifications = specificationsInput;
      const detailsOfStages = indexModel.results.specifications.detailsOfStages;
      const stagesWithLargeQ = detailsOfStages.find((item: StageDetailResults) => {
        return userEntryToNumber(item.stageQ.target) > 50;
      });
      if (stagesWithLargeQ) {
        result = {
          tab: FilterConstants.tabs.specifications,
          fields: [
            FilterConstants.fields.inputs.specifications.passbandFrequency,
            FilterConstants.fields.inputs.specifications.stopbandFrequency,
            FilterConstants.fields.inputs.specifications.centerFrequency,
          ],
          id: 'stageQTooLarge',
          parameters: { qValue: stagesWithLargeQ.stageQ.target },
        };
      }
    }
    return result;
  }

  // Validates fp values for Bandpass filters with Lowpass and Highpass stages
  // This case usually happens when the center frequency is set too low or too high
  // Inputs:
  //   - specificationsInput : Specification Input model
  //   - nameArrayOfFailedFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for Q factor (if any)
  _validateFp(specificationsInput: SpecificationInput, nameArrayOfFailedFields: Array<string>): ValidationError | null {
    let result = null;
    if (this.model.inputs.filterType === FilterConstants.filterType.bandPass) {
      if (nameArrayOfFailedFields.length === 0) {
        const indexModel = new IndexModel(this.model.opAmpSource);
        indexModel.inputs.filterType = this.model.inputs.filterType;
        indexModel.inputs.specifications = specificationsInput;
        const detailsOfStages = indexModel.results.specifications.detailsOfStages;
        for (let i = 0; i < detailsOfStages.length; i++) {
          const stage = detailsOfStages[i];
          const stageFilter = camelize(stage.filterName.substring(10).replace(/_/g, '-'));
          const stageFp = userEntryToNumber(stage.stageFp.target);
          if (
            stageFilter === FilterConstants.filterType.lowPass &&
            stageFp > _limits.passbandFrequency.lowPass.maximum
          ) {
            result = {
              tab: FilterConstants.tabs.specifications,
              fields: [
                FilterConstants.fields.inputs.specifications.passbandFrequency,
                FilterConstants.fields.inputs.specifications.centerFrequency,
              ],
              id: 'centerFrequencyTooHigh',
              parameters: {},
            };
            break;
          } else if (
            stageFilter === FilterConstants.filterType.highPass &&
            stageFp < _limits.stopbandFrequency.highPass.minimum
          ) {
            result = {
              tab: FilterConstants.tabs.specifications,
              fields: [
                FilterConstants.fields.inputs.specifications.passbandFrequency,
                FilterConstants.fields.inputs.specifications.centerFrequency,
              ],
              id: 'centerFrequencyTooLow',
              parameters: {},
            };
            break;
          }
        }
      }
    }
    return result;
  }

  // Checks if the tab has restrictions that cause it to disable the succeeding steps in the wizard
  // Returns: false - There are currently no restrictions for Specifications
  hasRestrictions(): boolean {
    return false;
  }
}
